import { api } from '../api';

// ========== ТИПЫ ==========

export interface OrderHistoryItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  image_url?: string;
  black_url?: string;
  sku?: string;
}

export interface OrderHistoryEntry {
  id: number;
  created_at: string;
  status: string;
  total?: number;
  total_amount?: number;
  delivery_cost?: number;
  /** null у заказов, оплата которых так и не прошла. */
  payment_status?: string | null;
  items?: OrderHistoryItem[];
  delivery?: {
    type?: string;
    address?: string;
    time?: string;
    cost?: number;
  };
}

/**
 * Позиция повтора: товар КАК СЕЙЧАС в каталоге + количество из прошлого заказа.
 * Форма описана явно, а не через ProductWithCategories: сервер отдаёт ровно эти
 * поля, и притворяться, что здесь полный товар витрины, значит однажды прочитать
 * из корзины поле, которого в ответе нет.
 */
export interface RepeatItem {
  id: number;
  name: string;
  description: string | null;
  /** В каталоге вес хранится и числом, и строкой вида «250 г» — берём как есть. */
  weight: number | string | null;
  sku: string | null;
  /** ТЕКУЩАЯ цена каталога — именно она попадёт в корзину. */
  price: number;
  image_url: string | null;
  black_url: string | null;
  is_visible: boolean;
  status: string;
  spiciness: string;
  is_set_of_the_day: boolean;
  categories: { id: number; name: string }[];
  quantity: number;
  /** Цена, по которой этот товар шёл в прошлом заказе. Только для показа разницы. */
  previous_price: number;
}

export interface RepeatRemoved {
  id: number;
  name: string;
  quantity: number;
  /** gone — товара больше нет в меню; unavailable — временно недоступен. */
  reason: 'gone' | 'unavailable';
}

export interface RepeatPriceChange {
  id: number;
  name: string;
  old_price: number;
  new_price: number;
}

export interface RepeatOrderPlan {
  order: { id: number; created_at: string };
  items: RepeatItem[];
  removed: RepeatRemoved[];
  price_changed: RepeatPriceChange[];
  /** Подарки и призы прошлого заказа — их выдают акции, а не история. */
  skipped_gifts: { id: number; name: string }[];
}

// ========== ЗАПРОСЫ ==========

/**
 * История заказов.
 *
 * По умолчанию сервер отдаёт только оплаченные. `includeUnpaid` добавляет
 * заказы, у которых оплата не прошла: без них человек, у которого сорвалась
 * оплата, не видит свой заказ нигде и оформляет второй.
 */
export async function getOrders(includeUnpaid = false): Promise<OrderHistoryEntry[]> {
  const response = await api.get<OrderHistoryEntry[] | { orders?: OrderHistoryEntry[] }>(
    includeUnpaid ? '/orders?include_unpaid=1' : '/orders'
  );
  const data = response.data;
  if (Array.isArray(data)) return data;
  return data?.orders ?? [];
}

/**
 * Только заказы с непрошедшей оплатой, без состава.
 *
 * Отдельная ручка нужна баннеру на главной. Полная история собирает состав
 * ОТДЕЛЬНЫМ запросом на каждый заказ, и на самой посещаемой странице это
 * стоило бы «1 + число заказов» запросов к базе на каждого авторизованного
 * посетителя. Карточке «Ожидает оплаты» состав не нужен — только номер, дата
 * и сумма.
 */
export async function getUnpaidOrders(): Promise<OrderHistoryEntry[]> {
  const response = await api.get<OrderHistoryEntry[] | { orders?: OrderHistoryEntry[] }>(
    '/orders?include_unpaid=1&unpaid_only=1'
  );
  const data = response.data;
  if (Array.isArray(data)) return data;
  return data?.orders ?? [];
}

/**
 * Состав прошлого заказа, сверенный с актуальным каталогом.
 * Считает сервер (routers/orders.js → GET /orders/:id/repeat): цены и наличие
 * — его зона ответственности, клиент только показывает результат.
 */
export async function getRepeatOrderPlan(orderId: number): Promise<RepeatOrderPlan> {
  const response = await api.get<RepeatOrderPlan>(`/orders/${orderId}/repeat`);
  return response.data;
}

export interface PaymentLinkResponse {
  order: { id: number; total: number };
  paymentUrl: string;
}

/** Ссылка на повторную оплату уже созданного заказа. */
export async function getOrderPaymentLink(orderId: number): Promise<PaymentLinkResponse> {
  const response = await api.get<PaymentLinkResponse>(`/orders/${orderId}/payment-link`);
  return response.data;
}

// ========== НЕОПЛАЧЕННЫЕ ЗАКАЗЫ ==========

/**
 * Сколько часов после оформления мы предлагаем доплатить.
 *
 * ВАЖНО: серверного автоотмена неоплаченных заказов НЕТ — строка в базе висит
 * бессрочно. Поэтому текст рядом с кнопкой не должен обещать, что заказ
 * «аннулируется сам». Это окно показа: свежий неоплаченный заказ ещё имеет
 * смысл доплатить, вчерашний — нет, его проще оформить заново по актуальным
 * ценам и часам работы.
 */
export const UNPAID_ORDER_WINDOW_HOURS = 24;

export function isUnpaid(order: OrderHistoryEntry): boolean {
  return order.payment_status !== 'paid';
}

/** Неоплаченные заказы свежее окна показа, новые сверху. */
export function selectPayableOrders(orders: OrderHistoryEntry[]): OrderHistoryEntry[] {
  const cutoff = Date.now() - UNPAID_ORDER_WINDOW_HOURS * 60 * 60 * 1000;
  return orders
    .filter((order) => {
      if (!isUnpaid(order)) return false;
      const created = new Date(order.created_at).getTime();
      // Битую дату не прячем: заказ реально не оплачен, кнопка полезнее.
      return Number.isNaN(created) || created >= cutoff;
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}
